import { BoardCard } from "components/board/board-card";
import { StatusColumnProps } from "types/board";
import { DataCardItem, DataListResponse } from "types/ui/card-item";
import moment from "moment";
import { StatusEnum, TaskStatusEnum } from "types/dict";
import { useEffect, useMemo, useRef } from "react";
import { useSharedState } from "state";

interface BoardProps {
  data: DataListResponse;
}

export default function BoardBackground({ data }: BoardProps) {
  const { searchQuery, handleSearch } = useSharedState();

  const prevMainPath = useRef("");
  const loadingRef = useRef<{ [key: number]: boolean }>({});
  const columnRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const observerRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    // Get the main route path without sub-routes
    const currentMainPath = window.location.pathname.split("/")[1];

    if (prevMainPath.current !== currentMainPath) {
      [0, 1, 2, 3, 4].forEach((status) => {
        if (columnRefs.current[status]) {
          columnRefs.current[status]!.scrollTop = 0;
        }
      });
      prevMainPath.current = currentMainPath;
    }
  }, [window.location.pathname]);

  useEffect(() => {
    const observers: { [key: number]: IntersectionObserver } = {};

    statusColumns.forEach((column) => {
      const observer = new IntersectionObserver(
        async (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !loadingRef.current[column.status]) {
            loadingRef.current[column.status] = true;
            try {
              await handleSearch(searchQuery, column.status);
            } finally {
              loadingRef.current[column.status] = false;
            }
          }
        },
        { threshold: 0.1 }
      );

      if (observerRefs.current[column.status]) {
        observer.observe(observerRefs.current[column.status]!);
      }

      observers[column.status] = observer;
    });

    return () => {
      Object.values(observers).forEach((observer) => observer.disconnect());
    };
  }, [handleSearch]);

  const isTaskBoard = useMemo(() => {
    return window.location.pathname.includes("tasks") ? true : false;
  }, [window.location.pathname]);

  const taskListMaxHeight = useMemo(() => {
    if (window.innerHeight >= 992) {
      return window.innerHeight - 175;
    } else if (window.innerHeight >= 768) {
      return window.innerHeight - 231;
    } else return "100%";
  }, [window.innerHeight]);

  const statusColumns: StatusColumnProps[] = [
    { title: "К выполнению", status: StatusEnum.TO_DO, color: "warning" },
    { title: "В работе", status: StatusEnum.IN_PROGRESS, color: "primary" },
    {
      title: isTaskBoard ? "На утверждении" : "Завершённые",
      status: isTaskBoard ? TaskStatusEnum.FOR_APPROVAL : StatusEnum.COMPLETED,
      color: isTaskBoard ? "purple" : "success",
    },
    {
      title: isTaskBoard ? "Завершённые" : "Отклонённые",
      status: isTaskBoard ? TaskStatusEnum.COMPLETED : StatusEnum.REJECTED,
      color: isTaskBoard ? "success" : "danger",
    },
  ];

  const getCardsByStatus = (status: number): DataCardItem[] =>
    isTaskBoard && status === TaskStatusEnum.TO_DO
      ? data[status]?.data.concat(data[TaskStatusEnum.REJECTED]?.data)
      : data[status]?.data;

  const getCardsCount = (status: number): number =>
    isTaskBoard && status === TaskStatusEnum.TO_DO
      ? data[status]?.total_count + data[TaskStatusEnum.REJECTED]?.total_count
      : data[status]?.total_count;

  const getDeadlineColor = (card: DataCardItem) => {
    if (isTaskBoard && card.status === TaskStatusEnum.COMPLETED) return "bg-success-lt";
    if (isTaskBoard && moment().diff(moment(card.dueDate), "days") > -3) return "text-white bg-danger-lt";
    if (isTaskBoard) return "bg-warning-lt";
    return "";
  };

  const shouldLoadNextPage = (status: number): boolean => {
    return data[status].total_count > data[status].data.length ? true : false;
  };

  return (
    <div className="row d-flex gap-2 mx-0 grow">
      {statusColumns.map((column) => (
        <div className={`col-12 col-md h-fit rounded-3 overflow-hidden bg-${column.color}-lt`} key={column.status}>
          <h2 className="mb-3">
            {column.title} - {getCardsCount(column.status) || 0}
          </h2>

          <div
            className="row row-cards mb-3 overflow-y-auto"
            style={{ maxHeight: taskListMaxHeight }}
            ref={(el) => (columnRefs.current[column.status] = el)}
          >
            {getCardsByStatus(column.status)?.length ? (
              <>
                {getCardsByStatus(column.status)?.map((card, index) => (
                  <BoardCard key={index} card={card} columnColor={column.color} getDeadlineColor={getDeadlineColor} />
                ))}

                {shouldLoadNextPage(column.status) && (
                  <div className="hidden" ref={(el) => (observerRefs.current[column.status] = el)}></div>
                )}
              </>
            ) : (
              <div className="text-center text-muted my-4">Нет соответствующих записей</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
